import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form as FinalForm, useField } from 'react-final-form'
import {
  DropdownOnSearchChangeData,
  Button,
  Segment,
  FormGroup
} from 'semantic-ui-react'
import debounce from 'debounce'

import { getDiff, getFormValidate } from '../../../../../../utils/form'
import {
  Form,
  Select,
  DateInput,
  Currency,
  Masked,
  Input,
  CustomRadio
} from '../../../../../../components/Form'
import { FormActionsContainer } from '../../../../../../styles/FormActionsContainer'
import { SelectOption } from '../../../../../../components/Form/Select'
import { pages } from '../../../../../../config/pages'
import { localeDateToDate } from '../../../../../../utils/date'

import {
  categoryToSelectOption,
  personToSelectOption
} from '../../../../utils/selectOptions'
import { SignatureFormatted } from '../../../../utils/formatSignature'
import {
  useSignature,
  useCategory,
  useCustomer,
  useSignatureCustomer
} from '../../hooks'
import { schema } from './schema'

const validateForm = getFormValidate(schema)

const initialValues = {
  recurrence: 'MONTHLY',
  discountPercent: 0
}

const DEBOUNCE_TIME = 500

const SignatureFormFields: React.FC = () => {
  const { signature, localChangeRecurrence } = useSignature()
  const {
    loading: loadingCategories,
    categories,
    listCategories
  } = useCategory()
  const categoryIdField = useField('categoryId')
  const { loading: loadingCustomers, customers, listCustomers } = useCustomer()
  const customerIdField = useField('customerId')
  const { loading: loadingCustomer, customer } = useSignatureCustomer()
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([])
  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([])
  const editing = !!signature

  useEffect(() => {
    listCategories()
  }, [])

  useEffect(() => {
    if (!customer) return
    setCustomerOptions([personToSelectOption(customer)])
  }, [customer])

  useEffect(() => {
    setCategoryOptions(categories.map(categoryToSelectOption))
  }, [categories])

  useEffect(() => {
    setCustomerOptions(customers.map(personToSelectOption))
  }, [customers])

  const searchCustomer = (data: DropdownOnSearchChangeData) => {
    const { searchQuery } = data

    return debounce(() => listCustomers(searchQuery), DEBOUNCE_TIME)()
  }

  return (
    <Segment>
      <Select
        name="categoryId"
        label="Categoria"
        options={categoryOptions}
        loading={loadingCategories}
        onChange={(e, data) => categoryIdField.input.onChange(data.value)}
        readOnly={editing}
        search
        required
        uppercase
      />
      <Select
        name="customerId"
        label="Cliente"
        options={customerOptions}
        loading={loadingCustomers || loadingCustomer}
        onSearchChange={(_, data) => searchCustomer(data)}
        onChange={(e, data) => customerIdField.input.onChange(data.value)}
        readOnly={editing}
        search
        required
        uppercase
      />
      <Input
        name="description"
        label="Descrição"
        maxLength={128}
        required
        uppercase
      />
      <FormGroup inline>
        <label>Recorrência</label>
        <CustomRadio
          name="recurrence"
          label="Mensal"
          value="MONTHLY"
          onChange={localChangeRecurrence}
        />
        <CustomRadio
          name="recurrence"
          label="Trimestral"
          value="QUARTERLY"
          onChange={localChangeRecurrence}
        />
        <CustomRadio
          name="recurrence"
          label="Semestral"
          value="SEMI_ANNUAL"
          onChange={localChangeRecurrence}
        />
        <CustomRadio
          name="recurrence"
          label="Anual"
          value="ANNUAL"
          onChange={localChangeRecurrence}
        />
      </FormGroup>
      <FormGroup>
        <Masked
          name="period"
          label="Período"
          mask="##/####"
          width={4}
          autoComplete="off"
          required
        />
        <DateInput
          name="dueDateFormatted"
          label="Vencimento"
          minDate={new Date()}
          width={12}
          required
        />
      </FormGroup>
      <FormGroup widths={3}>
        <Currency name="totalOfItemsFormatted" label="Valor total" readOnly />
        <Currency
          name="totalOfDiscountsFormatted"
          label="Valor dos descontos"
          readOnly
        />
        <Currency name="value" label="Valor da assinatura" readOnly />
      </FormGroup>
    </Segment>
  )
}

export const SignatureFields: React.FC = () => {
  const { loading, signature, createSignature, updateSignature } =
    useSignature()
  const [original, setOriginal] = useState<any>()
  const navigate = useNavigate()

  useEffect(() => {
    if (original) return
    if (!signature) return
    setOriginal(signature)
  }, [signature])

  function handleSubmit(values: SignatureFormatted) {
    console.log('values.period inside handleSubmit:', values.period)
    const {
      categoryId,
      customerId,
      description,
      recurrence,
      period,
      dueDateFormatted
    } = values
    const dueDate = localeDateToDate(dueDateFormatted)
    const [month, year] = [period.substring(0, 2), period.substring(2)]
    const periodInMMYYYY = year && month ? `${month}/${year}` : undefined

    if (!signature) {
      createSignature(
        {
          categoryId,
          customerId,
          description,
          recurrence,
          period: periodInMMYYYY,
          dueDate
        },
        (newSignature: SignatureFormatted) =>
          navigate(`${pages.signatures.path}/${newSignature.id}`)
      )
    }

    if (signature) {
      const diff = getDiff(original, {
        description,
        recurrence,
        period: periodInMMYYYY,
        dueDate
      })
      if (!diff) return
      updateSignature(diff, newSignature => {
        setOriginal(newSignature)
        navigate(pages.signatures.path)
      })
    }
  }

  return (
    <FinalForm
      onSubmit={handleSubmit}
      validate={validateForm}
      initialValues={
        { ...initialValues, ...signature } as Partial<SignatureFormatted>
      }
      validateOnBlur
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit} loading={loading}>
          <FormActionsContainer>
            <Button
              type="button"
              content="Voltar"
              as={Link}
              to={pages.signatures.path}
            />
            <Button type="submit" content="Salvar" floated="right" primary />
          </FormActionsContainer>
          <SignatureFormFields />
        </Form>
      )}
    </FinalForm>
  )
}
